import React from 'react';
import clsx from 'clsx';
import IconAlert from '@/assets/icons/icon-alert.svg?react';
import IconSpinner from '@/assets/icons/icon-spiner.svg?react';
import IconCancel from '@/assets/icons/icon-cancel.svg?react';
import { SecondaryButton } from '@/components/buttons/SecondaryButton/SecondaryButton';
import { PrimaryButton } from '@/components/buttons/PrimaryButton/PrimaryButton';
export interface ToastProps {
  text?: string;
  className?: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  onClick?: () => void;
  loading?: boolean;
  state?: 'default' | 'error' | 'warning' | 'success' | 'info';
  buttonSecondaryText?: string;
  buttonPrimaryText?: string;
  onButtonPrimaryClick?: () => void;
  onButtonSecondaryClick?: () => void;
  showCloseButton?: boolean;
  onClose?: () => void;
}

export const Toast: React.FC<ToastProps> = ({
  text,
  className = '',
  icon: IconOverride,
  onClick,
  loading = false,
  state = 'default',
  buttonSecondaryText,
  buttonPrimaryText,
  onButtonPrimaryClick,
  onButtonSecondaryClick,
  showCloseButton = false,
  onClose,
}) => {
  const IconEl = loading ? IconSpinner : IconOverride || IconAlert;
  const iconColor = loading
    ? ''
    : state === 'error'
      ? 'text-[#EA6559]'
      : state === 'warning'
        ? 'text-[#F5700B]'
        : state === 'success'
          ? 'text-[#10B96D]'
          : state === 'info'
            ? 'text-[#575F6A]'
            : 'text-coral-500';

  return (
    <div
      onClick={onClick}
      aria-busy={loading || undefined}
      className={clsx(
        'inline-flex h-9 items-center gap-2 rounded-lg bg-gray-900 py-2',
        // Dynamic horizontal padding:
        // Base left padding always 3 (pl-3). Right padding logic:
        // - If action button only (buttonSecondaryText && !showCloseButton): pr-1
        // - If both action and close buttons: pr-1.5
        // - Else (no action OR only close): default pr-3
        'pl-3',
        (buttonSecondaryText || buttonPrimaryText) && !showCloseButton && 'pr-1',
        (buttonSecondaryText || buttonPrimaryText) && showCloseButton && 'pr-1.5',
        !(buttonSecondaryText || buttonPrimaryText) && showCloseButton && 'pr-3',
        !(buttonSecondaryText || buttonPrimaryText) && !showCloseButton && 'pr-3',
        'cursor-default shadow-[0px_2px_8px_0px_rgba(0,0,0,0.20)]',
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <span
          className={clsx(
            'inline-flex h-4 w-4 items-center justify-center',
            loading && 'animate-spin',
          )}
          aria-hidden="true"
        >
          <IconEl className={clsx('h-4 w-4 fill-current', iconColor)} />
        </span>
        <span className="text-xs leading-none font-medium text-white">
          {text || (loading ? 'Loading…' : 'Save failed. Please try again.')}
        </span>
        {buttonSecondaryText && (
          <SecondaryButton
            text={buttonSecondaryText}
            size="sm"
            state="default"
            icon={null}
            onClick={onButtonSecondaryClick}
          />
        )}
        {buttonPrimaryText && (
          <PrimaryButton
            text={buttonPrimaryText}
            size="sm"
            state="default"
            icon={null}
            onClick={onButtonPrimaryClick}
          />
        )}

        {showCloseButton && (
          <button
            type="button"
            aria-label="Close"
            onClick={(e) => {
              e.stopPropagation();
              onClose?.();
            }}
            className={clsx(
              'inline-flex h-6 w-6 items-center justify-center rounded-md transition-all',
              'group cursor-pointer hover:bg-neutral-800',
            )}
          >
            <IconCancel className="h-4 w-4 text-neutral-400" />
          </button>
        )}
      </div>
    </div>
  );
};
